import {
  NavigationGuardProvider,
  useNavigationGuardContext,
} from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { WhatsappTemplateCategory } from "@jugl-web/rest-api";
import {
  BottomCenteredDrawer,
  BottomCenteredDrawerProps,
  Breadcrumb,
  ScreenTransitionWrapper,
} from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import {
  Screen,
  ScreenTransitionManagerProvider,
  useScreenTransitionManager,
} from "@jugl-web/utils/utils/ScreenTransitionManager";
import { usePrevious } from "@web-src/utils/helper";
import React, { ReactNode, useEffect, useMemo, ReactElement } from "react";
import { useInstagramAccount } from "../../providers/InstagramAccountProvider/useInstagramAccount";
import { useWhatsappAccount } from "../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { AIAgentInsightsScreen } from "./screens/AIAgentInsightsScreen/AIAgentInsightsScreen";
import { ConnectedAccountListScreen } from "./screens/ConnectedAccountListScreen";
import { InstagramAccountScreen } from "./screens/InstagramAccountScreen";
import { TwilioAccountScreen } from "./screens/TwilioAccountScreen";
import { VoiceAccountScreen } from "./screens/VoiceAccountScreen";
import { WebChatBotAccountScreen } from "./screens/WebChatBotAccountScreen";
import { WhatsappAccountScreen } from "./screens/WhatsappAccountScreen";
import { WhatsappAccountTab } from "./screens/WhatsappAccountScreen/types";
import { WhatsappTemplateFormScreen } from "./screens/WhatsappTemplateFormScreen";
import { ManageAccountsDrawerScreenToParametersMap } from "./types";
import { LiveChatAppAccountScreen } from "./screens/LiveChatAppAccountScreen";
import { EmailAccountScreen } from "./screens/EmailAccountScreen";
import { EmailSetupScreen } from "./screens/EmailSetupScreen";
import { EmailForwardingSetupScreen } from "./screens/EmailForwardingSetupScreen";
import { EmailDomainAuthSetupScreen } from "./screens/EmailDomainAuthSetupScreen";

interface ManageAccountsDrawerProps {
  isOpen: boolean;
  initialScreen?: Screen<ManageAccountsDrawerScreenToParametersMap>;
  onRequestClose: () => void;
}

const InnerManageAccountsDrawer: React.FC<ManageAccountsDrawerProps> = ({
  isOpen,
  initialScreen,
  onRequestClose,
}) => {
  const { instagramAccount } = useInstagramAccount();
  const { whatsappAgent } = useWhatsappAccount();

  const { isCx } = useAppVariant();
  const { t } = useTranslations();

  const { requestNavigation } = useNavigationGuardContext();

  const { screen, renderContent, transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const prevIsOpen = usePrevious(isOpen);

  const header = useMemo<BottomCenteredDrawerProps["header"]>(() => {
    const rootBreadcrumb: Breadcrumb = {
      id: "root",
      title: (
        <div className="flex items-center gap-3">
          <SettingsIcon />
          <BreadcrumbText>
            {isCx
              ? t({
                  id: "client-support-page.inbox-settings",
                  defaultMessage: "Inbox Settings",
                })
              : t({
                  id: "client-support-page.crm-settings",
                  defaultMessage: "CRM Settings",
                })}
          </BreadcrumbText>
        </div>
      ),
      onClick: () =>
        requestNavigation(() => transitionTo({ name: "connectedAccountList" })),
    };

    const emailAccountBreadcrumb: Breadcrumb = {
      id: "emailAccount",
      title: (
        <BreadcrumbText>
          {t({
            id: "client-support-page.email",
            defaultMessage: "Email",
          })}
        </BreadcrumbText>
      ),
    };

    const navigableEmailAccountBreadcrumb: Breadcrumb = {
      ...emailAccountBreadcrumb,
      onClick: () =>
        requestNavigation(() => transitionTo({ name: "emailAccount" })),
    };

    return renderContent<BottomCenteredDrawerProps["header"]>({
      aiAgentInsights: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb],
      },
      connectedAccountList: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb],
      },
      twilioAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "twilioAccount",
            title: (
              <BreadcrumbText>
                {t({
                  id: "common.twilio",
                  defaultMessage: "Twilio",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      voiceAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "voiceAccount",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.voice",
                  defaultMessage: "Phone calls",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      liveChatAppAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "liveChatAppAccount",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.live-chat-via-link",
                  defaultMessage: "Live Chat via Link",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      emailAccount: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb, emailAccountBreadcrumb],
      },
      emailSetup: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb, emailAccountBreadcrumb],
      },
      emailForwardingSetup: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          navigableEmailAccountBreadcrumb,
          {
            id: "emailForwardingSetup",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.email-forwarding",
                  defaultMessage: "Email Forwarding",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      emailDomainAuthSetup: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          navigableEmailAccountBreadcrumb,
          {
            id: "emailDomainAuthSetup",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.domain-auth",
                  defaultMessage: "Domain Auth",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      instagramAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          ...(instagramAccount
            ? [
                {
                  id: instagramAccount.id,
                  title: (
                    <BreadcrumbText>
                      {instagramAccount.username ||
                        instagramAccount.business_id}
                    </BreadcrumbText>
                  ),
                },
              ]
            : []),
        ],
      },
      webChatBotAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "customer-support",
            title: (
              <BreadcrumbText>
                {t({
                  id: "client-support-page.web-chat",
                  defaultMessage: "Web Chat",
                })}
              </BreadcrumbText>
            ),
          },
        ],
      },
      whatsappAccount: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          ...(whatsappAgent
            ? [
                {
                  id: whatsappAgent.waba_id,
                  title: <BreadcrumbText>{whatsappAgent.name}</BreadcrumbText>,
                },
              ]
            : []),
        ],
      },
      whatsappTemplateForm: ({ templateCategory }) => ({
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          ...(whatsappAgent
            ? [
                {
                  id: whatsappAgent.waba_id,
                  title: <BreadcrumbText>{whatsappAgent.name}</BreadcrumbText>,
                  onClick: () =>
                    requestNavigation(() =>
                      transitionTo({
                        name: "whatsappAccount",
                        tab:
                          templateCategory === WhatsappTemplateCategory.message
                            ? WhatsappAccountTab.messageTemplates
                            : WhatsappAccountTab.taskTemplates,
                      })
                    ),
                },
              ]
            : []),
          ...(templateCategory === WhatsappTemplateCategory.message
            ? [
                {
                  id: "message-template",
                  title: (
                    <BreadcrumbText>
                      {t({
                        id: "client-support-page.message-template",
                        defaultMessage: "Message Template",
                      })}
                    </BreadcrumbText>
                  ),
                },
              ]
            : [
                {
                  id: "message-template",
                  title: (
                    <BreadcrumbText>
                      {t({
                        id: "client-support-page.task-template",
                        defaultMessage: "Task Template",
                      })}
                    </BreadcrumbText>
                  ),
                },
              ]),
        ],
      }),
    });
  }, [
    instagramAccount,
    isCx,
    renderContent,
    requestNavigation,
    t,
    transitionTo,
    whatsappAgent,
  ]);

  const content = useMemo<ReactElement>(
    () =>
      renderContent({
        connectedAccountList: <ConnectedAccountListScreen />,
        aiAgentInsights: <AIAgentInsightsScreen />,
        twilioAccount: <TwilioAccountScreen />,
        voiceAccount: <VoiceAccountScreen />,
        liveChatAppAccount: <LiveChatAppAccountScreen />,
        emailAccount: <EmailAccountScreen />,
        emailSetup: ({ step }) => <EmailSetupScreen step={step} />,
        emailForwardingSetup: <EmailForwardingSetupScreen />,
        emailDomainAuthSetup: <EmailDomainAuthSetupScreen />,
        instagramAccount: ({ tab }) => <InstagramAccountScreen tab={tab} />,
        webChatBotAccount: () => <WebChatBotAccountScreen />,
        whatsappAccount: ({ tab }) => <WhatsappAccountScreen tab={tab} />,
        whatsappTemplateForm: ({ templateCategory, templateId }) => (
          <WhatsappTemplateFormScreen
            templateCategory={templateCategory}
            templateId={templateId}
          />
        ),
      }),
    [renderContent]
  );

  useEffect(() => {
    if (initialScreen && isOpen && !prevIsOpen) {
      transitionTo(initialScreen, { force: true });
    }
  }, [initialScreen, isOpen, prevIsOpen, transitionTo]);

  return (
    <BottomCenteredDrawer
      isOpen={isOpen}
      header={header}
      onClose={() => requestNavigation(onRequestClose)}
      className="h-[97vh] w-[97vw] max-lg:left-0 max-lg:h-full max-lg:w-full max-lg:translate-x-0 max-lg:rounded-none"
    >
      <BottomCenteredDrawer.Content className="flex min-h-0 flex-col">
        <ScreenTransitionWrapper
          screenName={screen.name}
          className="h-full min-h-full w-full"
        >
          {content}
        </ScreenTransitionWrapper>
      </BottomCenteredDrawer.Content>
    </BottomCenteredDrawer>
  );
};

export const ManageAccountsDrawer: React.FC<ManageAccountsDrawerProps> = (
  props
) => (
  <NavigationGuardProvider>
    <ScreenTransitionManagerProvider
      initialScreen={{ name: "connectedAccountList" }}
    >
      <InnerManageAccountsDrawer {...props} />
    </ScreenTransitionManagerProvider>
  </NavigationGuardProvider>
);

const BreadcrumbText = ({ children }: { children: ReactNode }) => (
  <span className="font-secondary text-lg text-[#383838]">{children}</span>
);
